import { Component, Input, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import {
    AppConfigService,
    AuthService,
    ErrorHandlerService,
    OrganizationService,
    SettingsService,
    ToastService
} from '../../services';

export interface IEmailCheckResponse {
    isUser: boolean;
    isCustomer: boolean;
    id: string;
    updateToken?: string;
    customerEmail?: string;
    organizationId?: string;
}

@Component({
    selector: 'app-login-modal',
    templateUrl: './login-modal.component.html',
    styleUrls: ['./login-modal.component.scss']
})
export class LoginModalComponent implements OnInit {
    @Input() isLandingPage: boolean = false;

    public loading: boolean = false;
    public showPasswordField: boolean = false;
    public checkingEmail: boolean = false;
    public emailCheckResponse: IEmailCheckResponse | undefined;
    public errorMessage: string = '';

    constructor(
        public appConfig: AppConfigService,
        private modalCtrl: ModalController,
        private router: Router,
        private auth: AuthService,
        private toast: ToastService,
        private errorHandler: ErrorHandlerService,
        private settings: SettingsService,
        private orgService: OrganizationService
    ) {}

    ngOnInit() {
    }

    dismiss() {
        void this.modalCtrl.dismiss({ loginSuccessful: false });
    }

    async login(form: NgForm) {
        if (form.invalid) {
            return;
        }

        this.loading = true;

        try {
            if (this.emailCheckResponse?.isUser) {
                await this.auth.logIn(form.value.email.toLowerCase(), form.value.password);
                this.auth.setUserLastLogin();
                this.auth.removeCustomerAuthData();
            } else if (this.emailCheckResponse?.isCustomer) {
                this.auth.setCustomerAuthData(this.emailCheckResponse);
            }

            this.auth.authenticate();
            this.settings.updateAppColors();

            void this.modalCtrl.dismiss({ loginSuccessful: true });
            void this.toast.showSuccess('Logged in successfully.');
        } catch (error: any) {
            console.error(error);
            this.errorMessage = this.errorHandler.handleAuthenticationErrors(error);
        } finally {
            this.loading = false;
        }
    }

    async handleEmailInput(event: any) {
        const email: string = event.detail.value;

        if (email === '') {
            return;
        }

        this.errorMessage = '';
        this.showPasswordField = false;

        this.checkingEmail = true;

        try {
            this.emailCheckResponse = await this.auth.checkExistingEmail(email);

            if (this.emailCheckResponse?.isUser) {
                this.showPasswordField = true;
            } else if (this.emailCheckResponse?.isCustomer) {
                this.emailCheckResponse.customerEmail = email;
                this.showPasswordField = false;
            } else {
                this.errorMessage = 'We don’t recognize this email, please try a different email or sign up!';
            }
        } catch(error: any) {
            console.error(error);
        } finally {
            this.checkingEmail = false;
        }
    }

    async signup() {
        await this.modalCtrl.dismiss();

        void this.router.navigate(['/' + this.orgService.organization.id + '/customer-signup']);
    }

    navigateToForgotPasswordPage(): void {
        void this.modalCtrl.dismiss();
        void this.router.navigate(['forgot-password']);
    }
}
